import React, { useEffect } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { useOnboarding } from './OnboardingContext';
import ClosedBook from './ClosedBook';

/**
 * Cinematic "Pottermore" opening:
 *   1. Book glides from its left landing spot to centre, scaling up.
 *   2. Slow zoom toward the spine.
 *   3. Front cover swings a FULL 180° around the Y-axis, exposing the cream inside.
 *   4. Hand off to the open-spread reveal.
 */
export default function OpeningTransition() {
    const { setPhase } = useOnboarding();

    const scale = useMotionValue(0.9);
    const x = useMotionValue(-140); // starts offset to the left (matches landing)
    const rot = useMotionValue(0);
    const opacity = useMotionValue(1);

    useEffect(() => {
        let cancelled = false;
        async function run() {
            // 1. Glide to centre + gentle scale up (kept small so it never exceeds viewport)
            animate(x, 0, { duration: 1.0, ease: [0.33, 0, 0.2, 1] });
            await animate(scale, 1.06, { duration: 1.0, ease: [0.33, 0, 0.2, 1] });
            if (cancelled) return;

            // 2. Swing the cover fully open (−180°) around the spine (left edge)
            await animate(rot, -180, { duration: 1.5, ease: [0.5, 0, 0.25, 1] });
            if (cancelled) return;

            // 3. settle to exactly 1.0 and cross-fade to the reveal spread (same footprint → no snap)
            animate(scale, 1.0, { duration: 0.5, ease: 'easeInOut' });
            await animate(opacity, 0, { duration: 0.5, delay: 0.2, ease: 'easeIn' });
            if (cancelled) return;

            setPhase('reveal');
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [setPhase, scale, x, rot, opacity]);

    return (
        <motion.div
            style={{ opacity, contain: 'layout' }}
            className="absolute inset-0 flex items-center justify-center z-40 overflow-hidden"
            data-testid="opening-transition"
        >
            <ClosedBook coverRotate={rot} containerScale={scale} containerX={x} dim={0} />
        </motion.div>
    );
}
